import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { f2 } from '../../copyFlow2'
import { refreshCount, type Card } from '../../lib/browse'
import { supabase, ITEM_COLUMNS } from '../../lib/api'
import type { PlanItemData } from '../../lib/plan'
import { AppHeader } from '../../ui/AppHeader'
import { Wordmark } from '../../ui/Wordmark'
import { TabBar, TOOLBAR } from '../../ui/TabBar'
import { Gear } from '../../ui/icons'
import { usePlanNav } from '../../ui/planNav'
import { BrowseTray } from './BrowseTray'
import { ListingDetail } from './ListingDetail'
import { NotForMePanel } from './TasteActions'
import { KindAskBar } from './KindChips'

/** Phone masthead (BRAND.md 8): the wordmark and Settings; it stays at the top (QA prod r1, B-054) with the filter summary under it. Browse / Saved / Itineraries live in the
 *  bottom tab bar on phones (Plan v3 blind test r1: the masthead tabs were unreachable once scrolled). */
export function PhoneNav() {
  const settings = usePlanNav()[3]
  return (
    <header data-review-id="phone-nav" className="sticky top-0 z-30 border-b border-line bg-bg pt-[env(safe-area-inset-top)] lg:hidden">
      <nav aria-label={f2('nav.settings')} className="mx-auto flex h-[60px] w-full max-w-md items-center justify-between px-5 md:max-w-[704px] md:px-8">
        <Wordmark size={22} />
        <Link to={settings.to} aria-label={settings.label} aria-current={settings.on ? 'page' : undefined} className="press -mr-2 inline-flex size-11 items-center justify-center rounded-[12px] text-ink-2 hover:bg-sunk">
          <Gear size={20} />
        </Link>
      </nav>
    </header>
  )
}

/** Look up one listing by id with the public key (a direct link, or a card no longer on screen). */
export async function fetchListing(id: string): Promise<Card | null> {
  // R-105 (Round 3, Lane 3): the booking note reads hours, schedule and ticket page too, and only the booking key of vibe
  // (the whole vibe stays out of this read). B-197: the photo and its credit too, or a linked listing opens without its photo
  const { data } = await supabase.from('items').select(`${ITEM_COLUMNS.replace(',vibe', '')},hours,hours_text,schedule_text,ticket_url,image_url,image_credit,booking:vibe->>booking`).eq('id', id).maybeSingle()
  if (!data) return null
  const { booking, ...row } = data as unknown as PlanItemData & { booking?: string | null }
  const item = { ...row, vibe: booking ? { booking } : null } as unknown as PlanItemData
  return { item, km: null, when: item.kind === 'event' && item.start_at ? { start: item.start_at, end: item.end_at } : null, added: false }
}

/**
 * The frame every browse page shares: navigation, the plan tray, and the detail opened with ?item= (so Back closes it
 * first and the list underneath keeps its place).
 */
export function BrowseShell({ children, cards, showDistance, tray = true }: { children: ReactNode; cards: Card[]; showDistance: boolean; tray?: boolean }) {
  const [q] = useSearchParams()
  const loc = useLocation()
  const navigate = useNavigate()
  const itemId = q.get('item')
  const fromState = (loc.state as { card?: Card } | null)?.card
  const [fetched, setFetched] = useState<Card | null>(null)
  const card = itemId ? (fromState?.item.id === itemId ? fromState : cards.find((c) => c.item.id === itemId) ?? (fetched?.item.id === itemId ? fetched : null)) : null

  useEffect(() => {
    void refreshCount()
  }, [])
  useEffect(() => {
    if (!itemId || card) return
    let live = true
    void fetchListing(itemId).then((c) => { if (live) setFetched(c) })
    return () => { live = false }
  }, [itemId, card])

  const close = useCallback(() => {
    if (fromState) navigate(-1)
    else {
      const next = new URLSearchParams(loc.search)
      next.delete('item')
      navigate({ pathname: loc.pathname, search: next.toString() }, { replace: true, preventScrollReset: true })
    }
  }, [fromState, loc.pathname, loc.search, navigate])

  return (
    <div className="min-h-dvh bg-bg">
      <AppHeader />
      <PhoneNav />
      {children}
      {/* room for the phone tab bar under the last card */}
      <div aria-hidden="true" className="lg:hidden" style={{ height: TOOLBAR }} />
      {tray && !card && <BrowseTray />}
      <TabBar />
      <ListingDetail card={card} onClose={close} showDistance={showDistance} />
      <NotForMePanel />
      <KindAskBar />
    </div>
  )
}
